import { useState, useCallback, useRef } from 'react';
import { toast } from '../components/ui/Toast';
import { handleUpsell } from '../utils/upsell';

// Compteur global des coûts pour la topbar (cross-component)
let globalCostListeners = [];
export const onGlobalCostUpdate = (listener) => {
    globalCostListeners.push(listener);
    return () => { globalCostListeners = globalCostListeners.filter(l => l !== listener); };
};
const notifyGlobalCost = (costData) => {
    globalCostListeners.forEach(l => l(costData));
};

export function useStreamChat() {
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingImages, setStreamingImages] = useState([]);
  const [streamingDocuments, setStreamingDocuments] = useState([]);
  const [streamingActions, setStreamingActions] = useState([]); // boutons d'action HITL
  const [streamingRubriques, setStreamingRubriques] = useState([]); // sélecteur rubrique HITL
  const [streamingDialecteMeta, setStreamingDialecteMeta] = useState(null); // S55 P1
  const [currentStep, setCurrentStep] = useState(null);
  const [completedGroups, setCompletedGroups] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamMeta, setStreamMeta] = useState(null);
  const [budgetAlert, setBudgetAlert] = useState(null);
  const [userKeyFailed, setUserKeyFailed] = useState(false);
  const [sessionMessageCount, setSessionMessageCount] = useState(0);
  const metaRef = useRef(null);
  const imagesRef = useRef([]);
  const documentsRef = useRef([]);
  const actionsRef = useRef([]); // boutons d'action HITL
  const actionRubriquesRef = useRef([]); // rubriques du sélecteur HITL (event 'actions')
  const dialecteMetaRef = useRef(null); // S55 P1
  const abortRef = useRef(null);
  const lastGroupRef = useRef(null);

  const sendStreamMessage = useCallback(async (url, body) => {
    setIsStreaming(true);
    setStreamingContent('');
    setStreamingImages([]);
    setStreamingDocuments([]);
    setStreamingActions([]);
    setStreamingRubriques([]);
    setStreamingDialecteMeta(null);
    setCurrentStep(null);
    setCompletedGroups([]);
    setStreamMeta(null);
    setBudgetAlert(null);
    setUserKeyFailed(false);
    metaRef.current = null;
    imagesRef.current = [];
    documentsRef.current = [];
    actionsRef.current = [];
    actionRubriquesRef.current = [];
    dialecteMetaRef.current = null;
    lastGroupRef.current = null;

    const token = localStorage.getItem('oracle_token');
    const controller = new AbortController();
    abortRef.current = controller;

    let fullContent = '';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        signal: controller.signal
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Erreur réseau' }));
        // Quota messages atteint / feature réservée → toast upsell dédié.
        handleUpsell(err);
        // ACCESS_V2 403 → payload { error:'forbidden', required_capability, message }
        // : préférer le message FR du backend plutôt que 'forbidden'.
        throw new Error(err.message || err.error || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') continue;

          try {
            const data = JSON.parse(raw);

            if (data.type === 'clear_stream') {
              fullContent = '';
              setStreamingContent('');
            } else if (data.type === 'status') {
              const { group, phase, label, tool, tools, elapsed_ms } = data;

              // Nouveau contrat group/phase
              if (group) {
                // Si un nouveau groupe commence, marquer le précédent comme complété
                if (phase === 'start' && lastGroupRef.current && lastGroupRef.current !== group) {
                  setCompletedGroups(arr => arr.includes(lastGroupRef.current) ? arr : [...arr, lastGroupRef.current]);
                }
                // Sur phase 'done', marquer ce groupe comme complété
                if (phase === 'done') {
                  setCompletedGroups(arr => arr.includes(group) ? arr : [...arr, group]);
                }
                if (phase === 'start') {
                  lastGroupRef.current = group;
                }
                // elapsed_ms (Data & Memory Map, Lot A) : latence réelle de la phase quand le backend la fournit
                setCurrentStep({ group, phase, label, tool: tool || null, tools: tools || null, elapsed_ms: elapsed_ms ?? null });
              } else {
                // Rétro-compatibilité ancien format step/label
                setCurrentStep({ group: data.step, phase: 'start', label: data.label, tool: null, tools: null });
              }
            } else if (data.type === 'chunk') {
              fullContent += data.content;
              setStreamingContent(fullContent);
            } else if (data.type === 'dialecte_applied') {
              // S55 P1 : metadata Dialecte émise par le backend AVANT les images.
              // Stocker pour que l'UI affiche le chip ✨ sous les images du message courant.
              const meta = {
                original: data.original,
                optimized: data.optimized,
                rationale: data.rationale,
                model_used: data.model_used,
                cost_usd: data.cost_usd,
                target_model: data.target_model,
              };
              dialecteMetaRef.current = meta;
              setStreamingDialecteMeta(meta);
            } else if (data.type === 'image') {
              imagesRef.current = [...imagesRef.current, data.url];
              setStreamingImages([...imagesRef.current]);
            } else if (data.type === 'document') {
              documentsRef.current = [...documentsRef.current, {
                url: data.url,
                filename: data.filename,
                size: data.size,
              }];
              setStreamingDocuments([...documentsRef.current]);
            } else if (data.type === 'actions') {
              // Boutons d'action HITL portant un payload exact (exécution déterministe).
              actionsRef.current = Array.isArray(data.items) ? data.items : [];
              setStreamingActions([...actionsRef.current]);
              // Rubriques du sélecteur HITL (slug+label, liste minuscule) — vide si le site
              // n'a pas de rubriques configurées (comportement legacy, pas de sélecteur).
              actionRubriquesRef.current = Array.isArray(data.rubriques) ? data.rubriques : [];
              setStreamingRubriques([...actionRubriquesRef.current]);
            } else if (data.type === 'done') {
              if (data.images?.length > 0) {
                imagesRef.current = [...imagesRef.current, ...data.images.filter(u => !imagesRef.current.includes(u))];
                setStreamingImages([...imagesRef.current]);
              }
              if (data.documents?.length > 0) {
                const existingUrls = documentsRef.current.map(d => d.url);
                const newDocs = data.documents.filter(d => !existingUrls.includes(d.url));
                documentsRef.current = [...documentsRef.current, ...newDocs];
                setStreamingDocuments([...documentsRef.current]);
              }
              metaRef.current = data;
              setStreamMeta(data);
              // Marquer le dernier groupe comme complété (saving)
              if (lastGroupRef.current) {
                setCompletedGroups(arr => arr.includes(lastGroupRef.current) ? arr : [...arr, lastGroupRef.current]);
              }
              // Ne PAS reset completedGroups ici — l'état final reste visible
              // Le reset se fait au prochain sendStreamMessage
              setCurrentStep(null);
              if (data.cost) notifyGlobalCost(data.cost);
              const msgCount = data.session_messages_count || data.conversation_messages_count || 0;
              if (msgCount > 0) setSessionMessageCount(msgCount);
            } else if (data.type === 'budget_warning') {
              setBudgetAlert({ type: 'warning', budget: data.budget });
            } else if (data.type === 'budget_exceeded') {
              setBudgetAlert({ type: 'exceeded', budget: data.budget });
            } else if (data.type === 'user_key_failed') {
              // S43 — clé OpenRouter user rejetée par OpenRouter, fallback plateforme
              setUserKeyFailed(true);
              toast.warning('Votre clé OpenRouter est invalide — appels basculés sur la clé plateforme');
            } else if (data.type === 'error') {
              throw new Error(data.error);
            }
          } catch (e) {
            // DIAG : un event actions qui échoue au parse (ligne SSE trop grosse / coupée ?)
            try { if (raw && raw.includes('"actions"')) toast.error(`🔔 DIAG: parse échoué sur event actions (len=${raw.length})`); } catch (_) {}
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return { aborted: true };
      throw err;
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }

    return {
      content: fullContent,
      images: imagesRef.current,
      documents: documentsRef.current,
      actions: actionsRef.current,
      rubriques: actionRubriquesRef.current,
      meta: metaRef.current,
      dialecteMeta: dialecteMetaRef.current, // S55 P1
    };
  }, []);

  const abort = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return {
    sendStreamMessage,
    streamingContent,
    streamingImages,
    streamingDocuments,
    streamingActions,
    streamingRubriques, // sélecteur rubrique HITL
    streamingDialecteMeta, // S55 P1
    currentStep,
    completedGroups,
    isStreaming,
    streamMeta,
    budgetAlert,
    userKeyFailed,
    sessionMessageCount,
    setSessionMessageCount,
    abort,
  };
}
